import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

export function CanonicalModal({
  open,
  title,
  eyebrow,
  description,
  size = 'large',
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  eyebrow?: string;
  description?: string;
  size?: 'medium' | 'large';
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    closeRef.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', escape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="ui-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`ui-modal ui-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <header className="ui-modal__header">
          <div>
            {eyebrow ? <span className="ui-modal__eyebrow">{eyebrow}</span> : null}
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="ui-modal__close"
            onClick={onClose}
            aria-label="Cerrar ventana"
          >
            <X aria-hidden="true" />
          </button>
        </header>
        <div ref={bodyRef} className="ui-modal__body">
          {children}
        </div>
      </section>
    </div>
  );
}

export function CanonicalNotice({
  message,
  tone = 'info',
  autoHideMs = 6000,
  onDismiss,
}: {
  message: string;
  tone?: 'info' | 'success' | 'error';
  autoHideMs?: number;
  onDismiss: () => void;
}) {
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);
  useEffect(() => {
    if (!message || autoHideMs <= 0) return;
    const timer = window.setTimeout(() => onDismissRef.current(), autoHideMs);
    return () => window.clearTimeout(timer);
  }, [autoHideMs, message]);
  if (!message) return null;
  return (
    <div className={`ui-notice ui-notice--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span>{message}</span>
      <button type="button" onClick={onDismiss} aria-label="Cerrar notificación">
        <X aria-hidden="true" />
      </button>
    </div>
  );
}
